import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useChatWindow } from '../App';
import ChatWindow from '../components/layout/ChatWindow';
import '../styles/pages/MessagesPage.css';
import { buildDisplayName, formatDisplayName } from '../utils/nameFormatting';

const CONVERSATIONS_API =
  'https://3mv40ugihi.execute-api.eu-west-2.amazonaws.com/dev/conversations';
const PUBLIC_PROFILE_API =
  'https://0zr99zyhbk.execute-api.eu-west-2.amazonaws.com/dev';

const conversationPartnerSub = (conversation) =>
  conversation.otherUserSub ||
  conversation.otherParticipantSub ||
  conversation.recipientUserSub ||
  null;

const formatConversationTime = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const value = new Date(timestamp);
  if (Number.isNaN(value.getTime())) {
    return '';
  }

  const today = new Date();
  const sameDay =
    value.getFullYear() === today.getFullYear() &&
    value.getMonth() === today.getMonth() &&
    value.getDate() === today.getDate();

  if (sameDay) {
    return value.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return value.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year:
      value.getFullYear() === today.getFullYear()
        ? undefined
        : '2-digit',
  });
};

export default function MessagesPage() {
  const { authFetch } = useAuth();
  const { openChats } = useWebSocket();
  const {
    isOpen,
    recipientUserSub,
    chatPartnerName,
    mode,
    openChatWithUser,
  } = useChatWindow();

  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await authFetch(CONVERSATIONS_API);
      if (!response.ok) {
        throw new Error(`API error ${response.status}`);
      }

      const payload = await response.json();
      const rawConversations = payload.conversations || [];

      const enriched = await Promise.all(
        rawConversations.map(async (conversation) => {
          const otherSub = conversationPartnerSub(conversation);
          let displayName =
            conversation.otherParticipantName ||
            conversation.otherUserName ||
            conversation.chatPartnerName ||
            '';

          if (!displayName && otherSub) {
            try {
              const profileResponse = await fetch(
                `${PUBLIC_PROFILE_API}/users/${otherSub}/profile`
              );

              if (profileResponse.ok) {
                const profile = await profileResponse.json();
                displayName = buildDisplayName(
                  profile.first_name,
                  profile.second_name
                );
              }
            } catch (profileError) {
              console.error(
                'Failed to load conversation participant profile',
                profileError
              );
            }
          }

          return {
            ...conversation,
            otherSub,
            displayName: formatDisplayName(displayName) || 'User',
          };
        })
      );

      enriched.sort((left, right) => {
        const leftTime = new Date(left.lastTimestamp || 0).getTime();
        const rightTime = new Date(right.lastTimestamp || 0).getTime();
        return rightTime - leftTime;
      });

      setConversations(enriched);
    } catch (loadError) {
      console.error('Failed to load conversations', loadError);
      setError('We could not load your conversations.');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const selectedSub =
    searchParams.get('conversation');

  useEffect(() => {
    if (loading || !conversations.length) {
      return;
    }

    const requestedSub = searchParams.get('conversation');

    if (requestedSub) {
      const conversation = conversations.find(
        (item) => item.otherSub === requestedSub
      );

      if (
        conversation &&
        (recipientUserSub !== requestedSub || mode !== 'embedded')
      ) {
        openChatWithUser(
          requestedSub,
          null,
          conversation.displayName,
          'embedded'
        );
      }

      return;
    }

    if (
      isOpen &&
      recipientUserSub &&
      mode !== 'embedded'
    ) {
      const existingConversation = conversations.find(
        (item) => item.otherSub === recipientUserSub
      );

      if (existingConversation) {
        setSearchParams(
          { conversation: recipientUserSub },
          { replace: true }
        );

        if (mode !== 'embedded') {
          openChatWithUser(
            recipientUserSub,
            null,
            existingConversation.displayName ||
              chatPartnerName ||
              'User',
            'embedded'
          );
        }
      }
    }
  }, [
    loading,
    conversations,
    searchParams,
    setSearchParams,
    isOpen,
    recipientUserSub,
    chatPartnerName,
    mode,
    openChatWithUser,
  ]);

  const visibleConversations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return conversations.filter((conversation) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        conversation.displayName
          .toLowerCase()
          .includes(normalizedSearch) ||
        (conversation.lastMessage || '')
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [conversations, searchTerm]);

  const conversationRows = useMemo(
    () =>
      visibleConversations.map((conversation) => {
        const openChat = (openChats || []).find(
          (chat) =>
            chat.recipientUserSub === conversation.otherSub
        );

        const liveMessages = openChat?.messages || [];
        const liveLastMessage =
          liveMessages[liveMessages.length - 1]?.text;

        return {
          ...conversation,
          preview:
            liveLastMessage ||
            conversation.lastMessage ||
            'No messages yet',
          unreadCount: openChat?.unreadCount || 0,
        };
      }),
    [visibleConversations, openChats]
  );

  const handleSelectConversation = (conversation) => {
    if (!conversation.otherSub) {
      return;
    }

    setSearchParams({
      conversation: conversation.otherSub,
    });

    openChatWithUser(
      conversation.otherSub,
      null,
      conversation.displayName,
      'embedded'
    );
  };

  return (
    <main className="messages-page">
      <div className="messages-shell">
        <aside
          className={`messages-sidebar${selectedSub ? ' messages-sidebar--has-selection' : ''}`}
        >
          <div className="messages-sidebar__header">
            <h1>Messages</h1>
            <label className="messages-search">
              <span className="sr-only">Search conversations</span>
              <input
                type="search"
                placeholder="Search conversations"
                value={searchTerm}
                onChange={(event) =>
                  setSearchTerm(event.target.value)
                }
              />
            </label>
          </div>

          <div className="conversation-list">
            {loading && (
              <div className="conversation-list__state">
                Loading conversations...
              </div>
            )}

            {!loading && error && (
              <div className="conversation-list__state conversation-list__state--error">
                <p>{error}</p>
                <button
                  type="button"
                  onClick={loadConversations}
                >
                  Try again
                </button>
              </div>
            )}

            {!loading &&
              !error &&
              conversations.length === 0 && (
                <div className="conversation-list__state">
                  No conversations yet.
                </div>
              )}

            {!loading &&
              !error &&
              conversations.length > 0 &&
              conversationRows.length === 0 && (
                <div className="conversation-list__state">
                  No conversations match your search.
                </div>
              )}

            {!loading &&
              !error &&
              conversationRows.map((conversation) => (
                <button
                  type="button"
                  key={
                    conversation.chatId ||
                    conversation.otherSub
                  }
                  className={`conversation-row${selectedSub === conversation.otherSub ? ' conversation-row--selected' : ''}`}
                  onClick={() =>
                    handleSelectConversation(conversation)
                  }
                >
                  <div className="conversation-row__avatar">
                    {conversation.displayName
                      .trim()
                      .charAt(0)
                      .toUpperCase() || '?'}
                  </div>

                  <div className="conversation-row__body">
                    <div className="conversation-row__topline">
                      <strong>
                        {conversation.displayName}
                      </strong>
                      <span>
                        {formatConversationTime(
                          conversation.lastTimestamp
                        )}
                      </span>
                    </div>

                    <div className="conversation-row__preview-line">
                      <span className="conversation-row__preview">
                        {conversation.preview}
                      </span>

                      {conversation.unreadCount > 0 && (
                        <span
                          className="conversation-row__unread"
                          aria-label={`${conversation.unreadCount} unread messages`}
                        >
                          {conversation.unreadCount > 99
                            ? '99+'
                            : conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </aside>

        <section
          className={`messages-conversation${selectedSub ? ' messages-conversation--active' : ''}`}
        >
          {selectedSub &&
          isOpen &&
          recipientUserSub === selectedSub ? (
            <>
              <button
                type="button"
                className="messages-mobile-back"
                onClick={() => setSearchParams({})}
              >
                Back to conversations
              </button>

              <ChatWindow
                embedded
                recipientUserSub={recipientUserSub}
                chatPartnerName={chatPartnerName}
              />
            </>
          ) : (
            <div className="messages-conversation__empty">
              <div>
                <h2>Your conversations</h2>
                <p>
                  Select a conversation to view your messages.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
